import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Loader2, AlertCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Return leg of Emergent-managed Google OAuth. Rendered by the
// AuthGateway in App.js whenever the URL fragment carries a session_id.
// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function AuthCallback() {
  const hasProcessed = useRef(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { refreshAuth } = useAuth();

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const run = async () => {
      const hash = window.location.hash || '';
      const match = hash.match(/session_id=([^&]+)/);
      const sessionId = match ? decodeURIComponent(match[1]) : '';
      // Scrub the session_id from the URL/history immediately.
      window.history.replaceState(null, '', window.location.pathname + window.location.search);

      if (!sessionId) {
        navigate('/login', { replace: true });
        return;
      }

      try {
        const resp = await axios.post(`${API_URL}/api/auth/google/session`, { session_id: sessionId });
        if (resp.data?.requires_2fa) {
          // Admin policy: TOTP required regardless of identity provider.
          const target = resp.data.enrollment_required ? '/totp-setup' : '/totp-challenge';
          navigate(target, { replace: true, state: { email: resp.data.email, rememberMe: true } });
          return;
        }
        await refreshAuth();
        navigate('/dashboard', { replace: true });
      } catch (e) {
        setError(e.response?.data?.detail || 'Google sign-in failed. Please try again.');
      }
    };
    run();
  }, [navigate, refreshAuth]);

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-[#E8F1E6] via-[#F4F7F2] to-white flex items-center justify-center px-4"
      data-testid="auth-callback-page"
    >
      <div className="bg-white rounded-2xl shadow-xl border border-[#E5E7EB] p-8 text-center max-w-md w-full">
        {error ? (
          <>
            <AlertCircle className="w-10 h-10 text-red-500 mx-auto mb-3" />
            <p className="text-[#14281D] font-semibold mb-2" data-testid="auth-callback-error">{error}</p>
            <Link to="/login" className="text-[#2F5D3A] hover:text-[#6FBF73] underline font-medium">
              Back to sign in
            </Link>
          </>
        ) : (
          <>
            <Loader2 className="w-10 h-10 text-[#6FBF73] animate-spin mx-auto mb-3" />
            <p className="text-[#14281D] font-semibold" data-testid="auth-callback-loading">
              Signing you in with Google…
            </p>
          </>
        )}
      </div>
    </div>
  );
}
